/**
 * IOT-HAL-BOOTSTRAP-HARDENING-1: Canonical NotImplementedAdapter.
 *
 * Every VendorAdapter method that cannot be fulfilled returns an explicit
 * `{ supported: false, reason: 'NOT_IMPLEMENTED', capability: '<name>' }`
 * result object rather than throwing or returning null.
 *
 * Identity and lifecycle methods always work correctly so HalService can
 * register, route, and inspect devices backed by unintegrated vendors.
 *
 * Use this as the fallback adapter when a device's vendor has no registered
 * adapter factory. It prevents `try/catch (e) { return null }` patterns.
 */

import { assertAdapterContract } from './VendorAdapter.js';

export class NotImplementedAdapter {
  /**
   * @param {object} device   - DeviceRecord (may be partial at boot)
   * @param {string} vendorId - VendorId for this adapter
   * @param {object} [capabilities] - Static capability matrix
   */
  constructor(device, vendorId, capabilities = {}) {
    this._device = device ?? {};
    this._vendorId = vendorId ?? 'unknown';
    this._capabilities = capabilities;
    this._lifecycleState = this._device.lifecycleState ?? 'provisioned';
  }

  // ── Identity (always functional) ─────────────────────────────────────────

  getDeviceId()       { return this._device.id ?? this._device.deviceId; }
  getVendor()         { return this._vendorId; }
  getModel()          { return this._device.model; }
  getLifecycleState() { return this._lifecycleState; }

  async setLifecycleState(state) {
    this._lifecycleState = state;
  }

  // ── Capability matrix (always functional) ────────────────────────────────

  getCapabilities() {
    return this._capabilities;
  }

  capabilities() {
    return this._capabilities;
  }

  // ── VendorAdapter interface — returns NOT_IMPLEMENTED result, never throws ─

  async probe() {
    return { ok: false, latencyMs: 0, error: 'NOT_IMPLEMENTED', supported: false, reason: 'NOT_IMPLEMENTED', capability: 'probe' };
  }

  async register() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'register' };
  }

  async send() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'send' };
  }

  async receive() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'receive', readings: [] };
  }

  async deregister() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'deregister' };
  }

  async fota() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'fota' };
  }

  // ── DeviceContract methods — return NOT_IMPLEMENTED, never throw ──────────

  async pushConfig() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'pushConfig' };
  }

  async getCurrentConfig() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'getCurrentConfig' };
  }

  async getPendingConfig() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'getPendingConfig' };
  }

  async rollbackConfig() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'rollbackConfig' };
  }

  async getTelemetry() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'getTelemetry', readings: [] };
  }

  subscribeToTelemetry() {
    return { unsubscribe: () => {}, supported: false, reason: 'NOT_IMPLEMENTED', capability: 'subscribeToTelemetry' };
  }

  async getDeviceHealth() {
    return { supported: false, reason: 'NOT_IMPLEMENTED', capability: 'getDeviceHealth' };
  }

  subscribeToEvents() {
    return { unsubscribe: () => {}, supported: false, reason: 'NOT_IMPLEMENTED', capability: 'subscribeToEvents' };
  }
}

/**
 * Factory function — returns a NotImplementedAdapter for any device.
 * Used by halBootstrap as the fallback when no vendor adapter is registered.
 *
 * @param {object} device
 * @param {string} vendorId
 * @param {object} [capabilities]
 * @returns {NotImplementedAdapter}
 */
export function createNotImplementedAdapter(device, vendorId, capabilities) {
  return new NotImplementedAdapter(device, vendorId, capabilities);
}
